import { Plane, TrainFront, Bus, Ship, Car, CarTaxiFront, Hotel, HandPlatter, MapPin, Route, Pencil, BedDouble } from "lucide-react";

/* Day-by-day calendar view of the trip timeline — keeps long trips (weeks/months) readable.
   Quiet days are compressed into a single muted row; hotel stays show as "still at …". */

const ICONS = { flight: Plane, train: TrainFront, bus: Bus, ferry: Ship, taxi: CarTaxiFront,
  car: Car, hotel: Hotel, pickup: MapPin, reservation: HandPlatter, other: Route };
const DAY_MS = 86400000;
const MAX_DAYS = 180;

const fmt = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined,
  { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const addDays = (iso, n) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * DAY_MS).toISOString().slice(0, 10);

export default function TimelineDayView({ trip, segments, readOnly, onEdit }) {
  const dated = segments.filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s.date || ""));
  const segDates = dated.flatMap((s) => [s.date, s.arrive_date]).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d || ""));
  const start = trip.start_date || (segDates.length ? segDates.reduce((a, b) => (a < b ? a : b)) : null);
  const end = trip.end_date || (segDates.length ? segDates.reduce((a, b) => (a > b ? a : b)) : null);
  if (!start || !end || end < start) {
    return <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-6 text-center">
      Add trip dates (or dated legs) to see the day-by-day view.</p>;
  }
  const total = Math.min(Math.round((new Date(`${end}T00:00:00Z`) - new Date(`${start}T00:00:00Z`)) / DAY_MS) + 1, MAX_DAYS);

  // build day rows, compressing quiet streaks
  const days = [];
  for (let i = 0; i < total; i++) {
    const d = addDays(start, i);
    const items = dated.filter((s) => s.date === d);
    const staying = segments.filter((s) => s.mode === "hotel" && s.date && s.arrive_date && s.date < d && d <= s.arrive_date);
    days.push({ n: i + 1, date: d, items, staying });
  }
  const rows = [];
  for (const day of days) {
    if (day.items.length === 0 && day.staying.length === 0) {
      const last = rows[rows.length - 1];
      if (last?.quiet) { last.to = day; continue; }
      rows.push({ quiet: true, from: day, to: day });
    } else {
      rows.push(day);
    }
  }

  return (
    <div className="space-y-1.5" data-testid="timeline-day-view">
      {rows.map((r, i) => r.quiet ? (
        <div key={i} className="flex items-center gap-3 px-3 py-2 border border-dashed border-zinc-200 bg-zinc-50/60 text-xs text-muted-foreground"
          data-testid={`day-quiet-${r.from.n}`}>
          <span className="font-mono font-bold uppercase tracking-wider shrink-0">
            Day {r.from.n}{r.to.n !== r.from.n ? `–${r.to.n}` : ""}
          </span>
          <span>{fmt(r.from.date)}{r.to.n !== r.from.n ? ` – ${fmt(r.to.date)}` : ""} · free — no legs planned</span>
        </div>
      ) : (
        <div key={i} className="flex gap-3 border border-zinc-300 bg-white p-3" data-testid={`day-row-${r.n}`}>
          <div className="w-16 shrink-0 border-r border-zinc-100 pr-3">
            <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-700">Day {r.n}</div>
            <div className="text-xs font-700 text-zinc-800 leading-tight mt-0.5">{fmt(r.date)}</div>
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            {r.staying.map((s) => (
              <div key={`stay-${s.id}`} className="flex items-center gap-1.5 text-xs text-teal-700" data-testid={`day-staying-${s.id}`}>
                <BedDouble className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">Still at <b>{s.from_place || "hotel"}</b>{s.arrive_date === r.date ? " · check-out today" : ""}</span>
              </div>
            ))}
            {r.items.map((s) => {
              const Icon = ICONS[s.mode] || Route;
              return (
                <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`day-seg-${s.id}`}>
                  <Icon className="w-4 h-4 text-primary shrink-0" />
                  {s.time && <span className="font-mono text-xs font-bold shrink-0">{s.time.slice(0, 5)}</span>}
                  {s.ref && <span className="font-mono text-xs font-bold text-zinc-700 shrink-0">{s.ref}</span>}
                  <span className="truncate font-medium">
                    {s.from_place}{s.to_place && <span className="text-zinc-400"> → </span>}{s.to_place}
                  </span>
                  {(s.seat || s.gate) && (
                    <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 shrink-0">
                      {[s.seat && `Seat ${s.seat}`, s.gate && `Gate ${s.gate}`].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {!readOnly && (
                    <button onClick={() => onEdit(s)} data-testid={`day-edit-${s.id}`}
                      className="ml-auto p-1 text-zinc-300 hover:text-primary shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {total === MAX_DAYS && <p className="text-[11px] text-muted-foreground">Showing the first {MAX_DAYS} days.</p>}
    </div>
  );
}
